// src/components/MinesweeperLobby.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import MinesweeperGame from './MinesweeperGame';

const MinesweeperLobby = ({ token }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [gameData, setGameData] = useState(null);
  const [user, setUser] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const userStr = localStorage.getItem('memo_user');
    if (userStr) setUser(JSON.parse(userStr));
  }, []);

  useEffect(() => {
    if (!token) return;

    const socket = io('/', { query: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => console.log('Minesweeper socket connected'));
    socket.on('connect_error', (err) => console.error('Socket error:', err));

    socket.on('minesweeper_game_start', (data) => {
      console.log('游戏开始数据:', data);
      setGameData(data);
      setStatus('playing');
    });

    // 移除 alert，让游戏组件自己处理结果
    // socket.on('minesweeper_game_result', ...)

    return () => {
      socket.disconnect();
    };
  }, [token]);

  const startMatch = () => {
    setStatus('matching');
    socketRef.current.emit('minesweeper_match_start', { token });
  };

  const cancelMatch = () => {
    socketRef.current.emit('minesweeper_cancel_match', { token });
    setStatus('idle');
  };

  const goBack = () => navigate('/game-center');

  if (status === 'playing' && gameData && user) {
    return (
      <MinesweeperGame
        room_id={gameData.room_id}
        rows={gameData.rows}
        cols={gameData.cols}
        initialBoard={gameData.board}
        initialMyRevealed={gameData.initialMyRevealed}
        initialOppRevealed={gameData.initialOppRevealed}
        initialMyFlags={gameData.initialMyFlags}
        socket={socketRef.current}
        userId={user.id}
        opponentId={gameData.opponentId}
      />
    );
  }

  // 匹配大厅 UI（样式同前，省略）
  return (
    <div style={styles.container}>
      <div style={styles.background} />
      <div style={styles.card}>
        <div style={styles.iconWrapper}>
          <span style={styles.icon}>💣</span>
          <span style={styles.icon}>💥</span>
          <span style={styles.icon}>🚩</span>
        </div>
        <h2 style={styles.title}>扫雷 · 双人对战</h2>
        <p style={styles.subtitle}>各自扫雷，先完成者胜！实时互看进度</p>

        {status === 'matching' ? (
          <div style={styles.matchingContainer}>
            <div style={styles.spinner} />
            <p style={styles.matchingText}>正在匹配对手...</p>
            <button onClick={cancelMatch} style={styles.cancelButton}>取消匹配</button>
          </div>
        ) : (
          <button onClick={startMatch} style={styles.startButton}>开始匹配</button>
        )}

        <button onClick={goBack} style={styles.backButton}>← 返回游戏中心</button>
      </div>
    </div>
  );
};

// 样式（保持原样，略）
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '20px',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)',
    zIndex: 0,
  },
  card: {
    position: 'relative',
    zIndex: 1,
    backgroundColor: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '30px',
    padding: '40px 30px',
    width: '100%',
    maxWidth: '450px',
    textAlign: 'center',
    boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  iconWrapper: { display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px' },
  icon: { fontSize: '48px', animation: 'float 2s ease-in-out infinite', display: 'inline-block' },
  title: { fontSize: '32px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '10px' },
  subtitle: { fontSize: '16px', color: '#7f8c8d', marginBottom: '30px' },
  startButton: {
    backgroundColor: '#2c3e50', color: 'white', border: 'none', padding: '14px 30px', fontSize: '18px',
    fontWeight: 'bold', borderRadius: '50px', cursor: 'pointer', width: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 5px 0 #1a252f', marginBottom: '20px',
  },
  backButton: { background: 'none', border: 'none', color: '#2c3e50', fontSize: '16px', cursor: 'pointer', textDecoration: 'underline', marginTop: '10px' },
  matchingContainer: { textAlign: 'center' },
  spinner: { width: '50px', height: '50px', border: '4px solid #e0e0e0', borderTopColor: '#2c3e50', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' },
  matchingText: { fontSize: '18px', color: '#2c3e50', marginBottom: '20px' },
  cancelButton: { backgroundColor: '#e74c3c', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '50px', cursor: 'pointer', fontSize: '16px' },
};

// 注入动画
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes float { 0% { transform: translateY(0px); } 50% { transform: translateY(-10px); } 100% { transform: translateY(0px); } }
`;
document.head.appendChild(styleSheet);

export default MinesweeperLobby;